import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon, DocumentTextIcon, AcademicCapIcon, BuildingLibraryIcon, FolderIcon, EnvelopeIcon, ClockIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';

const GlobalSearch = ({ className = '' }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const timer = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const performSearch = async (query) => {
    if (!query.trim()) return;

    setIsSearching(true);
    setShowResults(true);

    try {
      // Search across multiple content types
      const [articlesRes, coursesRes, universitiesRes, categoriesRes, contactsRes] = await Promise.allSettled([
        api.get(`/articles/admin?search=${encodeURIComponent(query)}&size=5`),
        api.get(`/courses?search=${encodeURIComponent(query)}&size=5`),
        api.get(`/universities?search=${encodeURIComponent(query)}&size=5`),
        api.get(`/categories/search?query=${encodeURIComponent(query)}`),
        api.get(`/contacts/admin?search=${encodeURIComponent(query)}&size=5`)
      ]);

      const results = [];

      // Process articles
      if (articlesRes.status === 'fulfilled' && Array.isArray(articlesRes.value?.data?.data)) {
        articlesRes.value.data.data.forEach(article => {
          results.push({
            id: article.id,
            type: 'article',
            title: article.title,
            description: article.brief || article.content?.substring(0, 100),
            icon: DocumentTextIcon,
            iconColor: 'text-indigo-600',
            iconBg: 'bg-indigo-100',
            path: `/articles/view/${article.id}`,
            date: article.created_at
          });
        });
      }

      // Process courses
      if (coursesRes.status === 'fulfilled' && coursesRes.value?.data?.data) {
        const courses = Array.isArray(coursesRes.value.data.data) ? coursesRes.value.data.data : coursesRes.value.data.data.courses || [];
        courses.forEach(course => {
          results.push({
            id: course.id,
            type: 'course',
            title: course.name || course.title,
            description: course.description?.substring(0, 100),
            icon: AcademicCapIcon,
            iconColor: 'text-cyan-600',
            iconBg: 'bg-cyan-100',
            path: `/courses`,
            date: course.created_at
          });
        });
      }

      // Process universities
      if (universitiesRes.status === 'fulfilled' && universitiesRes.value?.data?.data) {
        const universities = Array.isArray(universitiesRes.value.data.data) ? universitiesRes.value.data.data : universitiesRes.value.data.data.universities || [];
        universities.forEach(university => {
          results.push({
            id: university.id,
            type: 'university',
            title: university.name || university.title,
            description: university.description?.substring(0, 100),
            icon: BuildingLibraryIcon,
            iconColor: 'text-emerald-600',
            iconBg: 'bg-emerald-100',
            path: `/universities`,
            date: university.created_at
          });
        });
      }

      // Process categories
      if (categoriesRes.status === 'fulfilled' && categoriesRes.value?.data?.data?.results) {
        const categories = categoriesRes.value.data.data.results.slice(0, 5);
        categories.forEach(category => {
          results.push({
            id: category.id,
            type: 'category',
            title: category.name || category.title,
            description: category.description?.substring(0, 100),
            icon: FolderIcon,
            iconColor: 'text-amber-600',
            iconBg: 'bg-amber-100',
            path: `/categories`,
            date: category.created_at
          });
        });
      }

      // Process contacts
      if (contactsRes.status === 'fulfilled' && Array.isArray(contactsRes.value?.data?.data)) {
        contactsRes.value.data.data.forEach(contact => {
          results.push({
            id: contact.id,
            type: 'contact',
            title: contact.name || contact.email,
            description: contact.message?.substring(0, 100),
            icon: EnvelopeIcon,
            iconColor: 'text-rose-600',
            iconBg: 'bg-rose-100',
            path: `/contacts`,
            date: contact.created_at
          });
        });
      }

      setSearchResults(results);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultClick = (result) => {
    navigate(result.path);
    setSearchQuery('');
    setShowResults(false);
    setSearchResults([]);
  };

  const handleClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showResults || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          handleResultClick(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowResults(false);
        break;
      default:
        break;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getTypeLabel = (type) => {
    const labels = {
      article: 'Article',
      course: 'Course',
      university: 'University',
      category: 'Category',
      contact: 'Contact'
    };
    return labels[type] || type;
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Search articles, courses, universities..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => searchQuery && setShowResults(true)}
          className="w-full bg-slate-100 border-none outline-none rounded-xl pl-11 pr-10 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all"
        />
        {searchQuery && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-200 rounded-full transition-colors"
          >
            <XMarkIcon className="w-4 h-4 text-slate-400" />
          </button>
        )}
        {isSearching && (
          <div className="absolute right-10 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-indigo-600"></div>
          </div>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-200 max-h-96 overflow-y-auto z-50 animate-fadeIn">
          {searchResults.length > 0 ? (
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
              </div>
              {searchResults.map((result, index) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleResultClick(result)}
                  className={`w-full px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors text-left ${index === selectedIndex ? 'bg-indigo-50' : ''
                    }`}
                >
                  <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${result.iconBg} flex items-center justify-center`}>
                    <result.icon className={`w-5 h-5 ${result.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${result.iconBg} ${result.iconColor}`}>
                        {getTypeLabel(result.type)}
                      </span>
                      {result.date && (
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <ClockIcon className="w-3 h-3" />
                          {formatDate(result.date)}
                        </span>
                      )}
                    </div>
                    <h4 className="font-medium text-slate-800 text-sm mb-1 truncate">
                      {result.title}
                    </h4>
                    {result.description && (
                      <p className="text-xs text-slate-500 line-clamp-2">
                        {result.description}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          ) : searchQuery && !isSearching ? (
            <div className="px-4 py-8 text-center">
              <MagnifyingGlassIcon className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-600 font-medium mb-1">No results found</p>
              <p className="text-xs text-slate-400">Try different keywords or check your spelling</p>
            </div>
          ) : null}

          {/* Keyboard shortcuts hint */}
          {searchResults.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-xs text-slate-500 flex items-center justify-between">
              <span>Use ↑↓ to navigate, Enter to select, Esc to close</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
